import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { FormattedMessage } from "react-intl";
import axios from "axios";
import { useNavigate } from "react-router";
import { API_BASE_URL, API_URLS, LOCALSTORAGE_TOKEN_ITEM } from "constants/api";
import { Routes } from "constants/routes";
import singleCover from "assets/singleCover.png";
import ALBUMCover from "assets/epCover.png";
import epCover from "assets/ep.png";
import albumCover from "assets/albumCover.png";
import musicVideoCover from "assets/musicVideoCover.png";
import ringtoneCover from "assets/ringtoneCover.png";

type SimplePlan = {
  id: string;
  labelId: string;
  defaultLabel: string;
  price: number;
  text: string;
  image: string;
};

const PLANS: SimplePlan[] = [
  {
    id: "single",
    labelId: "NewRelease.Platform.PlanSingle",
    defaultLabel: "Single [1–2 tracks]",
    price: 9.99,
    text: "one time",
    image: singleCover,
  },
  {
    id: "ep",
    labelId: "NewRelease.Platform.PlanEP",
    defaultLabel: "EP [3–6 tracks]",
    price: 14.99,
    text: "one time",
    image: epCover,
  },
  {
    id: "album",
    labelId: "NewRelease.Platform.PlanAlbum",
    defaultLabel: "Album [7–12 tracks]",
    price: 24.99,
    text: "one time",
    image: ALBUMCover,
  },
  {
    id: "music-video",
    labelId: "NewRelease.Platform.PlanMusicVideo",
    defaultLabel: "Music video",
    price: 14.99,
    text: "one time",
    image: albumCover,
  },
  {
    id: "tiktok-sound",
    labelId: "NewRelease.Platform.PlanTikTokSound",
    defaultLabel: "Tik Tok sound",
    price: 4.99,
    text: "one time",
    image: musicVideoCover,
  },
  {
    id: "ringtone",
    labelId: "NewRelease.Platform.PlanRingtone",
    defaultLabel: "Ringtone",
    price: 4.99,
    text: "one time",
    image: ringtoneCover,
  },
];

export const Prices: React.FC = () => {
  const navigate = useNavigate();
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [releaseTypeIdByPlanId, setReleaseTypeIdByPlanId] = useState<Record<string, string>>({});

  const typeIdByPlanId: Record<string, string> = {
    single: "2",
    ep: "3",
    album: "1",
    "music-video": "4",
    "tiktok-sound": "7",
    ringtone: "6",
  };

  const mapApiTypeToPlanId = (apiItem: any): string | null => {
    const raw = String(
      apiItem?.type ||
        apiItem?.code ||
        apiItem?.slug ||
        apiItem?.name ||
        ""
    ).toUpperCase();

    if (!raw) return null;
    if (raw === "PODCAST") return null; // skip podcast

    if (raw === "SINGLE") return "single";
    if (raw === "EP") return "ep";
    if (raw === "ALBUM") return "album";
    if (raw === "CLIP" || raw === "MUSIC_VIDEO") return "music-video";
    if (raw === "TIKTOK_SOUND" || raw === "TIK_TOK_SOUND") return "tiktok-sound";
    if (raw === "RINGTONE") return "ringtone";

    return null;
  };

  useEffect(() => {
    const source = axios.CancelToken.source();

    const fetchReleaseTypes = async () => {
      try {
        const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM);
        const response = await axios.get(`${API_BASE_URL}${API_URLS.ReleaseTypes}`, {
          headers: {
            Accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          cancelToken: source.token,
        });

        const list = response.data?.data ?? response.data ?? [];
        if (!Array.isArray(list)) return;

        const next: Record<string, string> = {};
        list.forEach((item: any) => {
          const planId = mapApiTypeToPlanId(item);
          const rtid = item?.id != null ? String(item.id) : "";
          if (!planId || !rtid) return;
          next[planId] = rtid;
        });

        if (Object.keys(next).length) {
          setReleaseTypeIdByPlanId(next);
        }
      } catch (e) {
        if (axios.isCancel(e)) return;
        console.error("Failed to load release types for Prices:", e);
      }
    };

    fetchReleaseTypes();

    return () => {
      source.cancel("Prices unmounted");
    };
  }, []);

  const handlePlanClick = async (planId: string) => {
    const releaseTypeId = releaseTypeIdByPlanId[planId] || typeIdByPlanId[planId];
    if (!releaseTypeId || pendingId) return;

    const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM);
    if (!token) {
      // If not authenticated, send to sign-in.
      navigate(Routes.SignIn);
      return;
    }

    try {
      setPendingId(planId);
      const response = await axios.post(
        `${API_BASE_URL}${API_URLS.Releases}`,
        { release_type_id: releaseTypeId },
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const id = response.data?.data?.id;
      if (!id) return;

      const path = Routes.NewRelease.replace(":id?", id);
      navigate(`${path}?step=upload-image`);
    } catch (error) {
      console.error("Failed to create release from Prices:", error);
    } finally {
      setPendingId(null);
    }
  };

  return (
    <PageContainer>
      <Card>
        <Header>
          <HeaderTitle>Choose Your Plan</HeaderTitle>
        </Header>
        <PlansGrid>
          {PLANS.map((plan) => (
            <PlanCard
              key={plan.id}
              background={plan.image}
              onClick={() => handlePlanClick(plan.id)}
              $disabled={pendingId !== null}
            >
              <div>
                <FormattedMessage
                  id={plan.labelId}
                  defaultMessage={plan.defaultLabel}
                />
                <span>{plan.price}$</span>
                <PlanText>{plan.text}</PlanText>
              </div>
            </PlanCard>
          ))}
        </PlansGrid>
      </Card>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  width: 100%;
  padding: 18px 0 0 0;
`;

const Card = styled.div`
  width: 100%;
  border-radius: 25px;
  
  padding: 20px 22px 24px 22px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderTitle = styled.div`
  font-family: SF Pro Display;
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const HeaderTag = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
`;

const PlansGrid = styled.div`
  padding-top: 15px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 26px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PlanCard = styled.div<{ background: string; $disabled?: boolean }>`
  position: relative;
  height: 170px;
  border-radius: 12px;
  overflow: hidden;
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  background-image: url(${({ background }) => background});
  background-size: cover;
  background-position: center;
  transition: 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ $disabled }) =>
    !$disabled &&
    `
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 12px 28px rgba(15, 23, 42, 0.35);
    }
  `}

  div {
    position: relative;
    color: white;
    font-size: 1rem;
    font-weight: 600;
    text-align: center;
    z-index: 1;
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    gap: 7px;
    padding: 10px 16px;
    background: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(15 px);

    span {
      font-family: SF Pro Display;
      font-weight: 800;
      font-style: Heavy;
      font-size: 30px;
      line-height: 33px;
      letter-spacing: 0%;
      text-align: center;
      vertical-align: middle;
      color: #ffffff;
    }
  }
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 1;
`;

const PlanText = styled.span`
  font-family: SF Pro Display;
  font-weight: 500;
  font-size: 14px;
  line-height: 18px;
  letter-spacing: 0;
  color: #ffffffcc;
`;


